import React, { useEffect, useMemo, useState } from 'react'
import trainService, { TrainRunArtifact, TrainRunDetail } from '../services/trainService'
import datasetService from '../services/datasetService'
import { formatShortDatetime } from '../utils/date'

interface Props {
  isOpen: boolean
  onClose: () => void
  runId: number
  onChanged?: () => void | Promise<void>
}

export default function TrainRunDetailModal({ isOpen, onClose, runId, onChanged }: Props) {
  const [detail, setDetail] = useState<TrainRunDetail | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  // 고급 입력 섹션 제거
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [datasetPathLabel, setDatasetPathLabel] = useState<string>('-')

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      setError(null)
      setLoading(true)
      try {
        const d = await trainService.getRun(runId)
        setDetail(d)
      } catch (e: any) {
        setError(e?.message || '상세 조회 실패')
      } finally {
        setLoading(false)
      }
    })()
  }, [isOpen, runId])

  // Prevent background scroll when modal is open
  useEffect(() => {
    const prev = document.body.style.overflow
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
    document.body.style.overflow = prev
    return () => {}
  }, [isOpen])

  // Dataset 프로젝트/데이터셋 경로 라벨 (프로젝트명 > 데이터셋명)
  useEffect(() => {
    (async () => {
      try {
        const nodeId = detail?.dataset_node_id
        if (nodeId && nodeId > 0) {
          const list = await datasetService.listProjects()
          const ds = (list || []).find((n: any) => n.id === nodeId && n.kind === 'dataset')
          if (ds) {
            const project = (list || []).find((n: any) => n.id === ds.parent_id && n.kind === 'project')
            const label = [project?.name, ds.name].filter(Boolean).join(' > ')
            setDatasetPathLabel(label || ds.name || `Dataset #${nodeId}`)
          } else {
            setDatasetPathLabel(`Dataset #${nodeId}`)
          }
        } else {
          setDatasetPathLabel('-')
        }
      } catch {
        if (detail?.dataset_node_id) setDatasetPathLabel(`Dataset #${detail.dataset_node_id}`); else setDatasetPathLabel('-')
      }
    })()
  }, [detail?.dataset_node_id])

  const jsonPretty = (obj: any) => {
    try { return JSON.stringify(obj ?? {}, null, 2) } catch { return '{}' }
  }

  // formatShortDatetime은 공용 유틸 사용

  const formatDuration = (start?: string, end?: string): string | null => {
    if (!start || !end) return null
    try {
      const s = new Date(start).getTime()
      const e = new Date(end).getTime()
      if (Number.isNaN(s) || Number.isNaN(e) || e < s) return null
      let diff = Math.floor((e - s) / 1000)
      const h = Math.floor(diff / 3600); diff -= h * 3600
      const m = Math.floor(diff / 60); const sec = diff - m * 60
      const parts: string[] = []
      if (h) parts.push(`${h}시간`)
      if (m) parts.push(`${m}분`)
      if (!h && !m) parts.push(`${sec}초`)
      return parts.join(' ')
    } catch { return null }
  }

  const getPrimaryMetricString = (metrics?: Record<string, any> | null): string => {
    if (!metrics || Object.keys(metrics).length === 0) return '-'
    const labelMap: Record<string, string> = { accuracy: '정확도', f1: 'F1', auc_roc: 'AUC-ROC', precision: '정밀도', recall: '재현율', mse: 'MSE', mae: 'MAE', r2: 'R²', r_squared: 'R²' }
    const order = ['accuracy','f1','auc_roc','precision','recall','mse','mae','r2','r_squared']
    for (const key of order) {
      if (typeof (metrics as any)[key] !== 'undefined') {
        const v = (metrics as any)[key]
        const val = typeof v === 'number' ? (Math.abs(v) >= 1 ? v.toFixed(2) : v.toFixed(3)) : String(v)
        return `${labelMap[key]} ${val}`
      }
    }
    const k = Object.keys(metrics)[0]
    const v = (metrics as any)[k]
    const val = typeof v === 'number' ? (Math.abs(v) >= 1 ? v.toFixed(2) : v.toFixed(3)) : String(v)
    return `${k}: ${val}`
  }

  const getFrameworkLabelText = (d?: TrainRunDetail | null): string => {
    if (!d) return '-'
    const fw: any = d.training_env_info?.framework
    if (!fw) return '-'
    const name = fw?.name || fw?.Name || fw?.FRAMEWORK
    const ver = fw?.version || fw?.Version
    if (!name && !ver) return '-'
    return ver ? `${name} ${ver}` : String(name)
  }

  const deriveTaskType = (metrics?: Record<string, any> | null): 'Classification' | 'Regression' | '-' => {
    if (!metrics) return '-'
    const m = metrics as any
    if (typeof m.mse !== 'undefined' || typeof m.mae !== 'undefined' || typeof m.r2 !== 'undefined' || typeof m.r_squared !== 'undefined') return 'Regression'
    if (typeof m.accuracy !== 'undefined' || typeof m.f1 !== 'undefined' || typeof m.precision !== 'undefined' || typeof m.recall !== 'undefined' || typeof m.auc_roc !== 'undefined') return 'Classification'
    return '-'
  }

  const hp = useMemo(() => {
    const p = (detail?.parameters as any) || {}
    const h = p.hyperparameters || {}
    return {
      lr: h.learning_rate ?? h.lr,
      batch: h.batch_size ?? h.batch,
      epochs: h.epochs,
      opt: h.optimizer,
    }
  }, [detail?.parameters])

  const formatBytes = (n?: number) => {
    if (!n || n <= 0) return '-'
    const units = ['B','KB','MB','GB','TB']
    let idx = 0
    let val = n
    while (val >= 1024 && idx < units.length - 1) { val /= 1024; idx++ }
    return `${val.toFixed(1)} ${units[idx]}`
  }

  // 릴리즈 토글 제거

  const handleDownload = async (artifactId: number, fname?: string) => {
    try {
      setDownloadingId(artifactId)
      await trainService.downloadArtifact(artifactId, fname)
    } catch (e: any) {
      alert(e?.message || '다운로드 실패')
    } finally {
      setDownloadingId(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">Run #{runId}</h2>
            {detail && (
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-xs">{detail.status}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
          </div>
        </div>

        <div className="px-6 pb-6 flex-1 overflow-y-auto overscroll-contain">
        {loading ? (
          <div className="text-sm text-gray-500">불러오는 중...</div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : !detail ? (
          <div className="text-sm text-gray-500">데이터가 없습니다</div>
        ) : (
          <div className="space-y-5">
            {successMsg && (
              <div className="text-sm px-3 py-2 rounded bg-green-50 text-green-800 border border-green-200">{successMsg}</div>
            )}
            {/* 상단 요약 - 최소 정보만 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 text-sm text-gray-700">
                <div><span className="text-gray-500">데이터셋:</span> {datasetPathLabel}</div>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <div><span className="text-gray-500">생성일:</span> {formatShortDatetime(detail.created_at as any)}</div>
              </div>
            </div>

            

            {/* 핵심 입력 - 프로젝트(Task)에서 정의한 필수항목 값 */}
            <div className="border rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">핵심 입력</div>
              <div className="p-3 text-sm text-gray-700">
                {(() => {
                  const req = (detail.parameters as any)?.required || {}
                  const keys = Object.keys(req)
                  if (!keys || keys.length === 0) return <div className="text-gray-500">-</div>
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {keys.map(k => (
                        <div key={k}>
                          <div className="text-xs text-gray-500">{k}</div>
                          <div className="text-sm text-gray-900 break-words">{String((req as any)[k])}</div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </div>
            </div>

            {/* 기타 입력 */}
            <div className="border rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">기타 입력</div>
              <div className="p-3 text-sm text-gray-700">
                {(() => {
                  const extras = (detail.parameters as any)?.extras || {}
                  const keys = Object.keys(extras)
                  if (!keys || keys.length === 0) return <div className="text-gray-500">-</div>
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {keys.map(k => {
                        const raw: any = (extras as any)[k]
                        const text = (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim().length === 0)) ? '-' : String(raw)
                        return (
                          <div key={k}>
                            <div className="text-xs text-gray-500">{k}</div>
                            <div className="text-sm text-gray-900 break-words">{text}</div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            </div>

            {/* 학습기간 */}
            <div className="border rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">학습기간</div>
              <div className="p-3 text-sm text-gray-700">
                <div className="text-gray-900 leading-tight">
                  <div>시작: {formatShortDatetime(detail.started_at as any)}</div>
                  <div>종료: {formatShortDatetime(detail.finished_at as any)}</div>
                  <div>소요: {formatDuration(detail.started_at as any, detail.finished_at as any) || '-'}</div>
                </div>
              </div>
            </div>

            

            {/* 아티팩트 목록 */}
            <div className="border rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">아티팩트 파일</div>
              {detail.artifacts && detail.artifacts.length > 0 ? (
                <div className="max-h-64 overflow-auto divide-y">
                  {detail.artifacts.map(a => (
                    <div key={a.id} className="flex items-center gap-3 px-3 py-2 text-sm text-gray-800">
                      <div className="flex-1 truncate" title={a.file_name}>{a.file_name}</div>
                      <div className="text-xs text-gray-600">{a.file_type || '-'}</div>
                      <div className="text-xs text-gray-600">{formatBytes(a.file_size)}</div>
                      <button
                        className={`px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50 ${downloadingId === a.id ? 'border-gray-300 text-gray-500 cursor-wait' : 'border-gray-300'}`}
                        onClick={() => handleDownload(a.id, a.file_name)}
                        disabled={downloadingId === a.id}
                      >{downloadingId === a.id ? '다운로드 중...' : '다운로드'}</button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-6 text-sm text-gray-500">등록된 아티팩트가 없습니다.</div>
              )}
            </div>

            {/* 메모 */}
            <div className="border rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">메모</div>
              <div className="p-3 text-sm text-gray-700 break-words">{detail.notes && String(detail.notes).trim().length > 0 ? detail.notes : '-'}</div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  )
}


